import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Skeleton } from '../Skeleton';
import { Pagination } from './Pagination';
import type { DataTableProps } from './DataTable.types';

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  isLoading,
  emptyState,
  sort,
  onSort,
  pagination,
  onRowClick,
  className,
}: DataTableProps<T>) {
  const handleSort = (key: string) => {
    if (!onSort || !sort) return;

    if (sort.key === key) {
      onSort({
        key,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      onSort({
        key,
        direction: 'asc',
      });
    }
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  if (isLoading) {
    return (
      <div className={cn('w-full', className)}>
        <div className="border-b border-gray-100 dark:border-zinc-700 p-4">
          <Skeleton className="h-8 w-full" />
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-4 border-b border-gray-100 dark:border-zinc-700">
            <Skeleton className="h-6 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('w-full flex flex-col', className)}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          {columns.some((col) => col.width) && (
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} width={col.width} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr className="border-b border-gray-200 dark:border-zinc-700 bg-gray-50/50 dark:bg-zinc-800/50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'py-3 px-4 font-medium text-gray-500 dark:text-zinc-400 whitespace-nowrap',
                    col.sortable &&
                      'cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700/50 transition-colors select-none',
                    col.align ? alignClasses[col.align] : 'text-left',
                  )}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1.5 group',
                      col.align === 'center' && 'justify-center',
                      col.align === 'right' && 'justify-end',
                    )}
                  >
                    {col.header}
                    {col.sortable && (
                      <span className="inline-flex text-gray-400">
                        {sort?.key === col.key ? (
                          sort.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-500" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-500" />
                          )
                        ) : (
                          <ArrowUpDown
                            size={14}
                            className="opacity-0 group-hover:opacity-50 transition-opacity"
                          />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
            {data.length > 0 ? (
              data.map((item) => (
                <tr
                  key={keyExtractor(item)}
                  onClick={() => onRowClick?.(item)}
                  className={cn(
                    'group transition-colors',
                    onRowClick
                      ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/50'
                      : 'hover:bg-gray-50/50 dark:hover:bg-zinc-800/30',
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={`${keyExtractor(item)}-${col.key}`}
                      className={cn(
                        'p-4 text-gray-700 dark:text-zinc-300',
                        col.align ? alignClasses[col.align] : 'text-left',
                      )}
                    >
                      {col.render ? col.render(item) : (item as any)[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // Empty State Row
              <tr>
                <td colSpan={columns.length} className="p-8 text-center">
                  {emptyState || <div className="text-gray-500">Žádná data k zobrazení.</div>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && data.length > 0 && (
        <div className="border-t border-gray-100 dark:border-zinc-700">
          <Pagination {...pagination} />
        </div>
      )}
    </div>
  );
}
